import { useEffect } from "react";
import { useCommandPaletteStore } from "@/store/commandPalette/commandPaletteStore";

const IS_DEV = import.meta.env.DEV;

/**
 * Hook to register the Cmd/Ctrl+K keyboard shortcut for the command palette.
 * Call once at the app level.
 */
export const useCommandPaletteShortcut = () => {
  const toggle = useCommandPaletteStore((s) => s.toggle);
  const isOpen = useCommandPaletteStore((s) => s.isOpen);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const rawKey = typeof e.key === "string" ? e.key : "";
      if (!rawKey) {
        return;
      }

      const key = rawKey.toLowerCase();
      const isCommandK = (e.metaKey || e.ctrlKey) && !e.shiftKey && key === "k";
      const isCtrlShiftL =
        IS_DEV && e.ctrlKey && !e.metaKey && e.shiftKey && key === "l";

      if (!isCommandK && !isCtrlShiftL) {
        return;
      }

      if (e.defaultPrevented || e.repeat) {
        return;
      }

      const { target } = e;
      let isEditable = false;

      if (target instanceof HTMLElement) {
        const { tagName } = target;
        isEditable =
          tagName === "INPUT" ||
          tagName === "TEXTAREA" ||
          target.isContentEditable;
      }

      if (isEditable && !isOpen) {
        return;
      }

      e.preventDefault();
      toggle();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggle, isOpen]);
};
